import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getStorage } from '#utils/storage';
import { useAccountQuery, useLogger, useTracker } from './hooks';

const getCurrentInstallation = (installations, currentInstallationId) => {
  if (!installations || !currentInstallationId) return null;

  return installations.find((installation) => installation.id === currentInstallationId);
};

export const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
  const storage = getStorage();
  const {
    account,
    installations,
    loading,
    refetch,
  } = useAccountQuery();

  const [currentInstallationId, setCurrentInstallationId] = useState(
    storage.get('currentInstallationId'),
  );

  const currentInstallation = useMemo(() => getCurrentInstallation(
    installations,
    currentInstallationId,
  ), [installations, currentInstallationId]);

  const setCurrentInstallation = useCallback((installation) => {
    setCurrentInstallationId(installation.id);
    storage.set('currentInstallationId', installation.id);
  }, [storage]);

  useEffect(() => {
    if (!currentInstallation && installations?.length) {
      setCurrentInstallation(installations[0]);
    }
  }, [currentInstallation, installations, setCurrentInstallation]);

  useLogger({ account });
  useTracker({ account });

  const contextValue = useMemo(() => ({
    account,
    installations,
    loading,
    currentInstallation,
    refetch,
    setCurrentInstallation,
  }), [
    account,
    installations,
    currentInstallation,
    loading,
    refetch,
    setCurrentInstallation,
  ]);

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  );
};
